import { RateOwnerType, RateType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';

import { ItemColorLabelValue } from '~hooks/useRates/models';
import isEnumValue from '~utils/isEnumValue';

const rateTypeColorAndLabelKey = {
  [RateType.RATE_COMMISSION]: {
    color: 'secondary',
    labelKey: 'rate_types.rate_commission',
  },
  [RateType.RATE_FLAT_COMMISSION]: {
    color: 'info',
    labelKey: 'rate_types.rate_flat_commission',
  },
  [RateType.RATE_FLAT_RATE]: { color: 'info', labelKey: 'rate_types.rate_flat_rate' },
  [RateType.RATE_PER_BUSHEL]: { color: 'info', labelKey: 'rate_types.rate_per_bushel' },
  [RateType.RATE_PER_DAY]: { color: 'info', labelKey: 'rate_types.rate_per_day' },
  [RateType.RATE_PER_EACH]: { color: 'info', labelKey: 'rate_types.rate_per_each' },
  [RateType.RATE_PER_HOUR]: { color: 'info', labelKey: 'rate_types.rate_per_hour' },
  [RateType.RATE_PER_LOAD]: { color: 'info', labelKey: 'rate_types.rate_per_load' },
  [RateType.RATE_PER_POUND]: { color: 'info', labelKey: 'rate_types.rate_per_pound' },
  [RateType.RATE_PER_TON]: { color: 'info', labelKey: 'rate_types.rate_per_ton' },
  [RateType.RATE_PER_TONNE]: { color: 'info', labelKey: 'rate_types.rate_per_tonne' },
  [RateType.RATE_PER_YARD]: { color: 'info', labelKey: 'rate_types.rate_per_yard' },
} satisfies Record<RateType, { color: string; labelKey: string }>;

const rateTypeLabelColor = (type: RateType | undefined) => {
  if (type === undefined || !isEnumValue(RateType, type)) {
    return ItemColorLabelValue.parse({});
  }

  const { color, labelKey } = rateTypeColorAndLabelKey[type];

  return ItemColorLabelValue.parse({ color, label: t(labelKey), value: type });
};
const rateFilterUsingOwnerTypeOptions = {
  account: 'filter[account_id]',
  driverId: 'filter[driver_id]',
  equipment: 'filter[equipment_id]',
  includeDefaultRates: 'filter[include_default]',
  ownerType: 'filter[owner_type]',
  project: 'filter[project_id]',
  service: 'filter[service_id]',
  query: 'search[query]',
};

const DEFAULT_COMPANY_RATE_FAKE_ID = 'default_company_rate_id';

export {
  DEFAULT_COMPANY_RATE_FAKE_ID,
  rateFilterUsingOwnerTypeOptions,
  rateTypeLabelColor,
};

export const getRateTypeLabel = (type: RateType) => {
  const rateLabelWithColor = rateTypeLabelColor(type);
  return rateLabelWithColor?.label ?? '';
};

const CUSTOMER_EXCLUDED_RATE_TYPES: RateType[] = [
  RateType.RATE_COMMISSION,
  RateType.RATE_FLAT_COMMISSION,
];

// Per Day and Per Each are display-only: the rate form can't price them.
const rateTypeIsSelectable = {
  [RateType.RATE_COMMISSION]: true,
  [RateType.RATE_FLAT_COMMISSION]: true,
  [RateType.RATE_FLAT_RATE]: true,
  [RateType.RATE_PER_BUSHEL]: true,
  [RateType.RATE_PER_DAY]: false,
  [RateType.RATE_PER_EACH]: false,
  [RateType.RATE_PER_HOUR]: true,
  [RateType.RATE_PER_LOAD]: true,
  [RateType.RATE_PER_POUND]: true,
  [RateType.RATE_PER_TON]: true,
  [RateType.RATE_PER_TONNE]: true,
  [RateType.RATE_PER_YARD]: true,
} satisfies Record<RateType, boolean>;

const SELECTABLE_RATE_TYPES = Object.values(RateType).filter(
  (type) => rateTypeIsSelectable[type],
);

export const getRateTypeOptions = (ownerType?: RateOwnerType) =>
  SELECTABLE_RATE_TYPES.filter(
    (type) =>
      ownerType !== RateOwnerType.CUSTOMER ||
      !CUSTOMER_EXCLUDED_RATE_TYPES.includes(type),
  ).map((type) => {
    const rateLabel = getRateTypeLabel(type);
    return {
      label: rateLabel,
      value: type,
    };
  });

const HAULER_RATE_TYPES = [RateType.RATE_PER_HOUR, RateType.RATE_PER_TONNE];

export const getHaulerRateTypeOptions = () =>
  HAULER_RATE_TYPES.map((type) => {
    const rateLabel = getRateTypeLabel(type);
    return {
      label: rateLabel,
      value: type,
    };
  });

// currencyFormatter comes from the caller's useCompanyCurrency() so this stays
// an ordinary function: calling the hook in here would register it on whichever
// component happens to call getFormattedRateValue, violating the Rules of Hooks.
export const getFormattedRateValue = (
  type: RateType,
  value: number | null,
  currencyFormatter: (value: number | string) => string,
  showBlankForNull = false,
) => {
  if (value === null && showBlankForNull) {
    return '';
  }

  const displayValue = value ?? 0;

  if (type === RateType.RATE_COMMISSION) {
    return `${displayValue}%`;
  }

  return currencyFormatter(displayValue);
};

// Rate types denominated in a quantity or a duration, the only ones a quantity
// can multiply into a quote line total. Flat and commission rates price the
// whole job, so they have nothing to multiply.
const rateTypeIsPricedByQuantity = {
  [RateType.RATE_COMMISSION]: false,
  [RateType.RATE_FLAT_COMMISSION]: false,
  [RateType.RATE_FLAT_RATE]: false,
  [RateType.RATE_PER_BUSHEL]: true,
  [RateType.RATE_PER_DAY]: true,
  [RateType.RATE_PER_EACH]: true,
  [RateType.RATE_PER_HOUR]: true,
  [RateType.RATE_PER_LOAD]: true,
  [RateType.RATE_PER_POUND]: true,
  [RateType.RATE_PER_TON]: true,
  [RateType.RATE_PER_TONNE]: true,
  [RateType.RATE_PER_YARD]: true,
} as const satisfies Record<RateType, boolean>;

export type QuantityOrTimeRateType = {
  [Type in RateType]: (typeof rateTypeIsPricedByQuantity)[Type] extends true
    ? Type
    : never;
}[RateType];

export const QUANTITY_AND_TIME_RATE_TYPES = Object.values(RateType).filter(
  (type): type is QuantityOrTimeRateType => rateTypeIsPricedByQuantity[type],
);
